import { faCircleCheck, faCirclePlus } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { motion } from "framer-motion";

import { BlockchainIcon } from "~/components/logos/blockchain";
import { CTAPrimary } from "~/components/site/call-to-action-primary";
import { container, item, Layout } from "~/components/site/layout";
import { ROUTE_DATA } from "~/lib/constants";
import { BLOCKCHAINS, STABLECOINS } from "~/lib/ecosystem";
import { getMeta } from "~/lib/get-meta";
import { getUrls } from "~/lib/get-urls";

const { desc, path, title } = ROUTE_DATA.pricing;

type Plan = Readonly<{
  id: string;
  name: string;
  cta: string;
  href: string;
  price: string;
  heading: string;
  features: string[];
  addOns: string[];
}>;

const plans: Plan[] = [
  {
    id: "plan-business",
    name: "Business - Starting at",
    cta: "Create account",
    href: "https://app.brale.xyz/buy/signup/?ref=pricing",
    price: "$0",
    heading: "Use popular stablecoins",
    features: [
      "Mint and redeem stablecoins",
      "Custody stablecoins",
      "1:1 stablecoin swaps",
      "Bring your team",
    ],
    addOns: ["Automations"],
  },
  {
    id: "plan-starter",
    name: "Mini – Starting at",
    cta: "Apply",
    href: "https://app.brale.xyz/signup",
    price: "$10",
    heading: "Everything in Business plus:",
    features: [
      "Create a stablecoin",
      "Built-in on and off ramps",
      "Instant exchange listing",
      "Daily transparency reports",
    ],
    addOns: ["Dedicated VIP support", "Partner onboarding"],
  },
  {
    id: "plan-pro",
    name: "Pro – Starting at",
    cta: "Apply",
    href: "https://app.brale.xyz/signup",
    price: "$500",
    heading: "Everything in Mini plus:",
    features: [
      "Earn program revenue",
      "Dedicated VIP support",
      "Partner onboarding",
      "Token listing management",
    ],
    addOns: ["Subsidize stablecoin fees", "Third-party offramps"],
  },
  {
    id: "plan-custom",
    name: "Custom – Tailored to your needs",
    cta: "Talk to sales",
    href: "/contact",
    price: "Custom",
    heading: "Everything in Pro plus:",
    features: [
      "Custom blockchain integration",
      "Custom smart contracts",
      "Third-party reserve audits",
      "Custom treasury assets",
    ],
    addOns: [
      "Managed market making",
      "Embedded stablecoin on-ramp",
      "Managed global on-ramps & off-ramps",
      "Permissioned blockchains",
      "Custom funding methods",
      "Roadmap priority",
      "Bring your own reserve infrastructure",
      "Custom paymasters",
      "Managed custom blockchain",
    ],
  },
];

const globalAddOns: string[] = [
  "Send by Email",
  "Multi-chain custody",
  "Multi-chain compatibility",
  "Wallet management",
  "Ecosystem access",
  "API access",
  "Regulatory reporting",
  "OFAC/SDN updates",
  "Compliance scanning",
  "Alpha & Beta releases",
];

export function loader({ request }: LoaderFunctionArgs) {
  return {
    urls: getUrls({ path, request }),
  };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return data ? getMeta({ desc, title, urls: data.urls }) : [];
};

export default function Pricing(): JSX.Element {
  return (
    <Layout>
      <section className="section-first">
        <div className="container">
          <motion.div variants={item} className="pb-16">
            <h1 className="eyebrow">Pricing</h1>
            <h2 className="m-0">Flexible pricing to fit your program</h2>
          </motion.div>
          <div className="flex flex-col card-gap">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
              {plans.map((plan) => (
                <motion.div
                  variants={item}
                  key={plan.id}
                  className="bg-secondary round border border-primary"
                >
                  <div className="p-6 lg:p-8">
                    <span id={plan.id} className="eyebrow">
                      {plan.name}
                    </span>
                    <div className="mt-6 mb-0 flex items-baseline gap-x-1">
                      <span className="text-6xl font-semibold mb-8 text-primary">
                        {plan.price}
                      </span>
                      {plan.id !== "plan-custom" && (
                        <span className="text-sm font-semibold leading-6">
                          /month
                        </span>
                      )}
                    </div>
                    <a
                      href={plan.href}
                      aria-describedby={plan.id}
                      className="btn primary justify-center no-underline mb-10"
                    >
                      {plan.cta}
                    </a>
                    <h3 id={plan.id} className="eyebrow text-primary">
                      {plan.heading}
                    </h3>
                    <ul className="mt-6 space-y-3 text-sm leading-6 m-0 p-0">
                      {plan.features.map((feature) => (
                        <li
                          key={feature}
                          className="flex gap-x-3 m-0 p-0 text-base"
                        >
                          <FontAwesomeIcon
                            className="h-6 w-6 text-green-500"
                            icon={faCircleCheck}
                          />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {plan.addOns && (
                    <div className="border-t border-primary p-6 lg:p-8">
                      <h3 id={plan.id} className="eyebrow text-primary">
                        Add-ons
                      </h3>
                      <ul className="mt-6 space-y-3 text-sm leading-6 m-0 p-0">
                        {plan.addOns.map((addOn) => (
                          <li
                            key={addOn}
                            className="flex gap-x-3 m-0 p-0 text-base"
                          >
                            <FontAwesomeIcon
                              className="h-6 w-6 text-[#98A2B3]"
                              icon={faCirclePlus}
                            />
                            {addOn}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="flex flex-col gap-6"
            >
              <motion.div
                variants={item}
                className="bg-secondary round p-6 lg:p-8 border border-primary"
              >
                <h3 className="eyebrow text-primary">Features for everyone</h3>
                <ul className="gap-y-3 text-sm leading-6 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 m-0 p-0">
                  {globalAddOns.map((addOn, i) => (
                    <li key={i} className="flex gap-x-3 m-0 p-0 text-base">
                      <FontAwesomeIcon
                        className="h-6 w-6 text-green-500"
                        icon={faCircleCheck}
                      />
                      {addOn}
                    </li>
                  ))}
                </ul>
              </motion.div>
              <div className="flex flex-col sm:flex-row gap-6 w-full">
                <motion.div
                  variants={item}
                  className="flex-1 bg-secondary round p-6 lg:p-8 border border-primary"
                >
                  <h3 className="eyebrow text-primary">Supported chains</h3>
                  <div className="flex flex-wrap gap-2 xl:gap-4">
                    {BLOCKCHAINS.map((b, i) => (
                      <BlockchainIcon
                        key={i}
                        className="h-8 w-8 lg:h-10 lg:w-10 xl:-mr-2 border border-primary rounded-full"
                        chainId={b.chainId}
                      />
                    ))}
                  </div>
                </motion.div>
                <motion.div
                  variants={item}
                  className="flex-1 bg-secondary round p-6 lg:p-8 border border-primary"
                >
                  <h3 className="eyebrow text-primary">Supported assets</h3>
                  <div className="flex flex-wrap gap-2 xl:gap-4">
                    {STABLECOINS.filter((s) => s.id !== "mxne").map((s) => (
                      <a
                        key={s.ticker}
                        href={`/stablecoins/${s.ticker}`}
                        title={`Learn more about ${s.ticker}`}
                      >
                        <img
                          alt={`${s.name} logo`}
                          src={s.icon}
                          className="h-8 w-8 lg:h-10 lg:w-10 border border-primary rounded-full"
                        />
                      </a>
                    ))}
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      <motion.div
        variants={item}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <CTAPrimary
          heading="Still have questions?"
          subheading="Talk with sales to see how Brale can help you."
          primaryBtn="Talk to sales"
          primaryBtnUrl="/contact"
        />
      </motion.div>
    </Layout>
  );
}
